import {
    addPhotosToZipObj,
    getAssetsCsvArr,
    getProjectPropertiesCsvArr,
    getSpeciesData,
} from '@/js/util/shared_functionality.js';

import {
    centimetersToInches,
    forceDownload,
    inchesToCentimeters,
    roundAwayFloatingPointInaccuracies,
} from '@/js/util.js';

import { ConfirmationModal, ErrorModal, Modal } from '@/js/classes/modal.js';
import { AssetForm } from '@/js/classes/asset_form.js';
import { AssetType, AttributeType } from '@/js/util/shared_constants.js';
import { parseFloatStrict } from '@/js/util/asserts.js';

import { captureException as sentryCaptureException } from '@sentry/browser';
import { zipSync } from 'fflate';
import { stringify as csvStringify } from 'csv-stringify/browser/esm/sync';


async function treeBenefitsRequest(project) {
    const species_attribute = project.getAttribute(AssetType.Tree, 'species');
    const tree_payloads = {};
    for(const asset of project.getAssets()) {
        if(asset.getValue('species').startsWith('custom_species:')) { continue; }
        const species_data = getSpeciesData(species_attribute['options'][asset.getValue('species')]);
        if(species_data['engine_compat'] === false) { continue; }
        if(parseFloatStrict(asset.getValue('dbh')) < 1) { continue; }

        const coordinates = asset.getValue('coordinates');
        tree_payloads[asset.asset_id] = {
            'longitude': coordinates[0],
            'latitude': coordinates[1],
            'diameter': asset.getValue('dbh'), // dbh in cm
            'species': asset.getValue('species'), // species code
            'condition': asset.getValue('condition').toLowerCase(), // lowercase string
            'crown-exposure': asset.getValue('sun_exposure'), // number
            'years': 1,
        };
    }

    const response = await fetch(`${ITREE_API}/bulk-tree-benefits/?key=${ITREE_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tree_payloads),
    });

    if(!response.ok) { throw new Error(`API returned status ${response.status}`); }

    return await response.json();
}

async function getBenefitsAssetsCsvArr(project) {
        let trees_not_processed = [];
        try {
            const assets_csv_arr = getAssetsCsvArr(project, AssetType.Tree);
            const asset_id_index = assets_csv_arr[0].indexOf('Asset ID');

            const response_data = await treeBenefitsRequest(project);
            trees_not_processed = response_data['failed'] ?? [];
            const benefits_data = (response_data['results'] || {});

            // Must match order of asset_benefits_values below
            const asset_benefits_columns = [
                'biomass_kg',
                'co2_stored_kg',
                'co2_stored_worth_usd',
                'co2_sequestered_kg',
                'co2_sequestered_worth_usd',
                'no2_kg',
                'no2_worth_usd',
                'co_kg',
                'co_worth_usd',
                'o3_kg',
                'o3_worth_usd',
                'pm25_kg',
                'pm25_worth',
                'so2_kg',
                'so2_worth',
                'rainfall_intercepted_cu_m',
                'runoff_avoided_cu_m',
                'runoff_avoided_worth_usd',
            ];

            for(let i = 1; i < assets_csv_arr.length; i++) {
                const asset_id = assets_csv_arr[i][asset_id_index];
                const asset_benefits = benefits_data[asset_id];
                if(!asset_benefits) { continue; }

                const annual = asset_benefits['data']['total']['benefits']['annual']['category'][0];

                // Must match order of asset_benefits_columns above
                const asset_benefits_values = [
                    annual['carbon']['biomass'],
                    annual['carbon']['carbon-dioxide']['storage'],
                    annual['carbon']['storage-worth-lifetime'],
                    annual['carbon']['carbon-dioxide']['sequestration'],
                    annual['carbon']['sequestration-worth'],
                    annual['pollution-removed']['no2'],
                    annual['pollution-removed']['no2-worth'],
                    annual['pollution-removed']['co'],
                    annual['pollution-removed']['co-worth'],
                    annual['pollution-removed']['o3'],
                    annual['pollution-removed']['o3-worth'],
                    annual['pollution-removed']['pm25'],
                    annual['pollution-removed']['pm25-worth'],
                    annual['pollution-removed']['so2'],
                    annual['pollution-removed']['so2-worth'],
                    annual['hydrology']['interception'],
                    annual['hydrology']['runoff-avoided'],
                    annual['hydrology']['runoff-avoided-worth'],
                ];

                assets_csv_arr[i] = assets_csv_arr[i].concat(asset_benefits_values);
            }

            assets_csv_arr[0] = assets_csv_arr[0].concat(asset_benefits_columns);
            return assets_csv_arr;

        } catch(err) {
            console.log('Error requesting tree benefits', err);
            sentryCaptureException(err);
            new ErrorModal(`<strong>Error requesting tree benefits:</strong> ${err.message}`).show();
            return [];

        } finally {
            if(trees_not_processed.length > 0) { console.error('Some trees were missing benefits: ', trees_not_processed.join()); }
        }
    }


const generateFile = (async (project, data_to_download) => {
    const encoder = new TextEncoder();
    const zipObj = {};
    const project_csv_rows = [];
    const project_identifier = project.getIdentifier();

    if(data_to_download.includes('Photos')) {
        await addPhotosToZipObj(project, zipObj);
    }

    if(data_to_download.includes('Project Properties')) {
        project_csv_rows.push(...getProjectPropertiesCsvArr(project));
        project_csv_rows.push([]); // Spacer row
    }

    if(data_to_download.includes('Tree Benefits')) {
        project_csv_rows.push(...await getBenefitsAssetsCsvArr(project));
    } else if(data_to_download.includes('Trees')) {
        project_csv_rows.push(...getAssetsCsvArr(project, AssetType.Tree));
    }

    if(project_csv_rows.length > 0) {
        zipObj['CollectorProjectData_'+project_identifier+'.csv'] = encoder.encode(csvStringify(project_csv_rows));
    }

    if(data_to_download.includes('Project Backup')) {
        zipObj['CollectorProjectBackup_'+project_identifier+'.json'] = encoder.encode(JSON.stringify(await project.getBackupObj(), null, 4));
    }

    const zip_file_entries = Object.entries(zipObj);
    if(zip_file_entries.length === 1) {
        const [entry_filename, entry_content] = zip_file_entries[0];
        forceDownload(entry_filename, entry_content, 'text/plain');
        return;
    }

    const zip_file_data = zipSync(zipObj);
    forceDownload('CollectorData_'+project_identifier+'.zip', zip_file_data, 'application/zip');
});

export const downloadTreeProjectData = (async (project) => {
    const SETTINGS_DEFAULTS = {
        'tree_data_file': ['Trees'],
        'other_files': ['Project Backup'],
    };
    const SETTINGS_ATTRIBUTES = [
        {
            'category_name': '',
            'attribute_type': AttributeType.ButtonMultiOption,
            'name': 'Tree Data CSV File:',
            'internal_name': 'tree_data_file',
            'neverWrap': true,
            'options': [
                'Project Properties',
                'Trees',
                'Tree Benefits',
            ],
        },
        {
            'category_name': '',
            'attribute_type': AttributeType.ButtonMultiOption,
            'name': 'Other Files:',
            'internal_name': 'other_files',
            'neverWrap': true,
            'options': [
                'Project Backup',
                'Photos',
            ],
        },
    ];

    const settings_modal = new ConfirmationModal('Download Project');
    const settings_form_elem = document.createElement('div');
    const settings_form = new AssetForm({
        'form_elem': settings_form_elem,
        'attributes': SETTINGS_ATTRIBUTES,
        'values': SETTINGS_DEFAULTS,
    });

    settings_modal.appendHtml(`
        <ul>
            <li>Select which types of data you want to download</li>
            <li>Blue buttons are already selected by default</li>
            <li>Benefits may take a while to load if you have a lot of trees</li>
        </ul>
    `);

    settings_form.generateForm();
    settings_modal.appendNode(settings_form_elem);

    settings_modal.onConfirm(async () => {
        const values = settings_form.validateForm();
        if(values !== false) {
            const data_to_download = [ ...values['tree_data_file'], ...values['other_files'] ];

            if(data_to_download.length === 0) {
                alert('You must select at least one thing to download.');
                return;
            }
            if(!data_to_download.includes('Project Backup')) {
                alert('You are not downloading a project backup. The files you are downloading may not help in the event of data loss.');
            }

            try {
                settings_modal.disableConfirmationButton();
                settings_modal.startLoading();
                await generateFile(project, data_to_download);
                settings_modal.stopLoading();
                settings_modal.enableConfirmationButton();
            } catch(err) {
                console.log(err);
                sentryCaptureException(err);
                settings_modal.stopLoading();
                settings_modal.enableConfirmationButton();
            }
        }
    });

    settings_modal.show();
});

export const convertDbhFromStoredToDisplay = (inputNumber) => {
    if(window.newcollector.settings.units === 'English') { inputNumber = centimetersToInches(inputNumber); } // Convert from centimeters to inches
    if(window.newcollector.settings.trunk_measurement === 'Circumference') { inputNumber = roundAwayFloatingPointInaccuracies(inputNumber * Math.PI); } // Convert from Diameter to Circumference
    return inputNumber;
}

export const convertDbhFromDisplayToStored = (inputNumber) => {
    if(window.newcollector.settings.units === 'English') { inputNumber = inchesToCentimeters(inputNumber); } // Convert from inches to centimeters
    if(window.newcollector.settings.trunk_measurement === 'Circumference') { inputNumber = roundAwayFloatingPointInaccuracies(inputNumber / Math.PI); } // Convert from Circumference to Diameter
    return inputNumber;
}

export const getProjectTreeBenefitsTotal = (async (project) => {
    const loading_modal = new Modal('Loading Benefits');
    loading_modal.appendHtml('<p>If your project has a lot of trees, this may take a while.</p>');
    const num_assets = project.numAssets();
    loading_modal.show();

    const total_benefits = {
        'biomass': 0,
        'stormwater_managed': 0,
        'stormwater_managed_monetary': 0,
        'co2_sequestered': 0,
        'co2_sequestered_monetary': 0,
        'co2': 0,
        'co2_monetary': 0,
        'no2': 0,
        'no2_monetary': 0,
        'co': 0,
        'co_monetary': 0,
        'o3': 0,
        'o3_monetary': 0,
        'pm25': 0,
        'pm25_monetary': 0,
        'so2': 0,
        'so2_monetary': 0,
    };

    try {
        const response_data = await treeBenefitsRequest(project);
        let trees_not_processed = response_data['failed'] ? response_data['failed'].length : 0;
        for(const result of Object.values(response_data['results'] || {})) {
            try {
                const annual = result['data']['total']['benefits']['annual']['category'][0];
                total_benefits['biomass'] += annual['carbon']['biomass'];
                total_benefits['stormwater_managed'] += annual['hydrology']['runoff-avoided'];
                total_benefits['stormwater_managed_monetary'] += annual['hydrology']['runoff-avoided-worth'];
                total_benefits['co2_sequestered'] += annual['carbon']['carbon-dioxide']['sequestration'];
                total_benefits['co2_sequestered_monetary'] += annual['carbon']['sequestration-worth'];
                total_benefits['co2'] += annual['carbon']['carbon-dioxide']['storage'];
                total_benefits['co2_monetary'] += annual['carbon']['storage-worth-lifetime'];
                total_benefits['no2'] += annual['pollution-removed']['no2'];
                total_benefits['no2_monetary'] += annual['pollution-removed']['no2-worth'];
                total_benefits['co'] += annual['pollution-removed']['co'];
                total_benefits['co_monetary'] += annual['pollution-removed']['co-worth'];
                total_benefits['o3'] += annual['pollution-removed']['o3'];
                total_benefits['o3_monetary'] += annual['pollution-removed']['o3-worth'];
                total_benefits['pm25'] += annual['pollution-removed']['pm25'];
                total_benefits['pm25_monetary'] += annual['pollution-removed']['pm25-worth'];
                total_benefits['so2'] += annual['pollution-removed']['so2'];
                total_benefits['so2_monetary'] += annual['pollution-removed']['so2-worth'];

            } catch(e) {
                trees_not_processed++;
            }
        }

        loading_modal.remove();
        if(trees_not_processed > 0) { new ErrorModal(`Some trees (<strong>${trees_not_processed}</strong> of <strong>${num_assets}</strong> total) were not processed.`).show(); }

    } catch(err) {
        sentryCaptureException(err);
        loading_modal.remove();
        new ErrorModal('Error while handling API response. Contact support.').show();
    }

    return { 'total_benefits': total_benefits };
});